import api from './api';

export interface Plan {
  _id: string;
  name: string;
  description: string;
  features: string[];
  price: number;
  duration: number; // in days
  maxProjects: number;
  maxUsers: number;
  status: 'active' | 'inactive';
  createdAt?: Date;
  updatedAt?: Date;
}

export interface PlanListResponse {
  success: boolean;
  count: number;
  pagination: {
    total: number;
    page: number;
    pages: number;
    limit: number;
  };
  data: Plan[];
}

export interface PlanResponse {
  success: boolean;
  data: Plan;
}

export interface PlanQueryParams {
  page?: number;
  limit?: number;
  status?: 'active' | 'inactive';
  search?: string;
}

class PlanService {
  /**
   * Get all plans
   */
  async getPlans(params: PlanQueryParams = {}): Promise<PlanListResponse> {
    try {
      const response = await api.get<PlanListResponse>('/plans', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching plans:', error);
      throw error;
    }
  }

  /**
   * Get plan by ID
   */
  async getPlan(planId: string): Promise<Plan> {
    try {
      const response = await api.get<PlanResponse>(`/plans/${planId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching plan:', error);
      throw error;
    }
  }

  /**
   * Create plan
   */
  async createPlan(data: Partial<Plan>): Promise<Plan> {
    try {
      const response = await api.post<PlanResponse>('/plans', data);
      return response.data.data;
    } catch (error) {
      console.error('Error creating plan:', error);
      throw error;
    }
  }

  /**
   * Update plan
   */
  async updatePlan(planId: string, data: Partial<Plan>): Promise<Plan> {
    try {
      const response = await api.put<PlanResponse>(`/plans/${planId}`, data);
      return response.data.data;
    } catch (error) {
      console.error('Error updating plan:', error);
      throw error;
    }
  }

  /**
   * Delete plan
   */
  async deletePlan(planId: string): Promise<void> {
    try {
      await api.delete(`/plans/${planId}`);
    } catch (error) {
      console.error('Error deleting plan:', error);
      throw error;
    }
  }

  /**
   * Toggle plan status (active/inactive)
   */
  async togglePlanStatus(planId: string, status: 'active' | 'inactive'): Promise<Plan> {
    try {
      const response = await api.patch<PlanResponse>(
        `/plans/${planId}/status`, 
        { status }
      );
      return response.data.data;
    } catch (error) {
      console.error('Error updating plan status:', error);
      throw error;
    }
  }
}

export default new PlanService();